import React, { useEffect, useState, useRef } from 'react';
import { Chessboard } from 'react-chessboard';


interface SquareStyles {
  [key: string]: React.CSSProperties;
}

interface ChessboardComponentProps {
  position: string;
  onPieceDrop: (sourceSquare: string, targetSquare: string) => boolean;
  lastMove: {from: string, to: string} | null;
  boardOrientation: 'white' | 'black';
  isThinking: boolean;
  isGameActive: boolean;
}

const ChessboardComponent: React.FC<ChessboardComponentProps> = ({
  position,
  onPieceDrop,
  lastMove,
  boardOrientation,
  isThinking,
  isGameActive
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [boardWidth, setBoardWidth] = useState(0);
  const [selectedSquare, setSelectedSquare] = useState<string | null>(null);
  const [showStartMessage, setShowStartMessage] = useState(false);


  // Responsive board sizing
  useEffect(() => {
    const updateBoardWidth = () => {
      if (containerRef.current) {
        // Calcola lo spazio disponibile tenendo conto del padding interno (p-3 = 0.75rem x 2 = 1.5rem)
        const paddingSpace = 24; // 12px (0.75rem) per lato
        const availableWidth = containerRef.current.clientWidth - paddingSpace;
        setBoardWidth(availableWidth);
      }
    };

    // Setup resize observer
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateBoardWidth);
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  // Create custom square styles to highlight the last move and selected square
  const customSquareStyles: SquareStyles = {};
  if (lastMove) {
    // Highlight source square
    customSquareStyles[lastMove.from] = {
      backgroundColor: 'rgba(225, 225, 0, 0.15)',
      boxShadow: 'inset 0 0 5px 5px rgba(225, 225, 0, 0.05)',
    };
    
    // Highlight target square
    customSquareStyles[lastMove.to] = {
      backgroundColor: 'rgba(225, 225, 0, 0.2)',
      boxShadow: 'inset 0 0 5px 5px rgba(225, 225, 0, 0.1)',
    };
  }

  // Add highlight for the selected square
  if (selectedSquare) {
    customSquareStyles[selectedSquare] = {
      backgroundColor: 'rgba(0, 255, 0, 0.3)',
      boxShadow: 'inset 0 0 5px 5px rgba(0, 255, 0, 0.1)',
    };
  }

  // Handle piece drop
  const handlePieceDrop = (sourceSquare: string, targetSquare: string) => {
    const moveSuccess = onPieceDrop(sourceSquare, targetSquare);
    if (!moveSuccess) {
      if (!isGameActive) {
        setShowStartMessage(true);
        setTimeout(() => setShowStartMessage(false), 1000);
      }
    }
    return moveSuccess;
};

  // Handle click on square
  const handleSquareClick = (square: string) => {
    // If we have a piece selected already, try to move it
    if (selectedSquare) {
      // If clicking on the same square, deselect it
      if (square === selectedSquare) {
        setSelectedSquare(null);
        return;
      }
      
      // Try to make a move from selectedSquare to square
      const moveSuccess = onPieceDrop(selectedSquare, square);
      if (!moveSuccess) {
        if (!isGameActive) {
          setShowStartMessage(true);
          setTimeout(() => setShowStartMessage(false), 1000);
        }
      }
      
      // Reset selection after move attempt
      setSelectedSquare(null);
    } else {
      // First click - select the square if it has a piece
      // We check if the current position has a piece on this square
      const fen = position.split(' ')[0];
      const board = fenToBoard(fen);
      const [file, rank] = square.split('');
      const fileIndex = 'abcdefgh'.indexOf(file);
      const rankIndex = 8 - parseInt(rank);
      
      if (board[rankIndex][fileIndex] !== '') {
        setSelectedSquare(square);
      }
    }
  };
  
  // Helper function to convert FEN to board representation
  const fenToBoard = (fen: string): string[][] => {
    const rows = fen.split('/');
    const board: string[][] = [];
    
    for (const row of rows) {
      const boardRow: string[] = [];
      for (const char of row) {
        if (isNaN(parseInt(char))) {
          boardRow.push(char);
        } else {
          for (let i = 0; i < parseInt(char); i++) {
            boardRow.push('');
          }
        }
      }
      board.push(boardRow);
    }
    
    return board;
  };

  return (
    <div 
      ref={containerRef} 
      className="relative border-2 rounded border-terminal-green p-3 shadow-[0_0_20px_rgba(0,255,0,0.2)] w-full max-w-[500px]"
    >
      {boardWidth > 0 && (
        <Chessboard 
          position={position} 
          onPieceDrop={handlePieceDrop} 
          boardWidth={boardWidth}
          customDarkSquareStyle={{ backgroundColor: '#1a3a1a' }}
          customLightSquareStyle={{ backgroundColor: '#2a4a2a' }}
          customSquareStyles={customSquareStyles}
          boardOrientation={boardOrientation}
          showBoardNotation={false}
          onSquareClick={handleSquareClick}
        />
      )}
      {isThinking && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/80 p-4 rounded font-vt323 text-2xl blink text-center">
          AI is thinking...
        </div>
      )}
      {showStartMessage && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/80 p-4 rounded font-vt323 text-2xl blink text-center">
          Press Start to play
        </div>
      )}
    </div>
  );
};

export default ChessboardComponent;